import { StyleSheet, View } from 'react-native';
import { Button, Icon, Text, useTheme } from 'react-native-paper';
import { useTranslation } from 'react-i18next';

// ALL-134: the owner pressed «Cancelar» on the GPS contract. Nothing was
// charged, so this is information, not an error: neutral container colours,
// never theme.colors.error. Actions (only those with a handler are shown), in
// the approved order:
//   1. Continuar só com Controlo de Horas (primary): same licences and billing
//      period, goes to the Hours payment.
//   2. Rever o contrato: opens the contract again.
//   3. Voltar ao chat.
// The expired paywall shows the message only (no handlers): its plan list
// already offers Hours and it has no chat.

export interface GpsContractDeclinedNoticeProps {
  onContinueHoursOnly?: () => void;
  onReviewContract?: () => void;
  onReturnToChat?: () => void;
  /** The Hours switch is in flight: the Hours button spins, every action is disabled. */
  busy?: boolean;
  /** Inline failure of the Hours switch; the actions stay so the owner can retry. */
  errorMessage?: string | null;
  testID?: string;
}

export const MIN_TOUCH_HEIGHT = 44;

export function GpsContractDeclinedNotice({
  onContinueHoursOnly,
  onReviewContract,
  onReturnToChat,
  busy = false,
  errorMessage,
  testID = 'gps-decline',
}: GpsContractDeclinedNoticeProps) {
  const { t } = useTranslation('onboarding');
  const theme = useTheme();
  const content = { minHeight: MIN_TOUCH_HEIGHT };
  const hasActions = Boolean(onContinueHoursOnly || onReviewContract || onReturnToChat);

  return (
    <View style={styles.root} testID={testID}>
      <View
        style={[styles.message, { backgroundColor: theme.colors.secondaryContainer }]}
        accessibilityLiveRegion="polite"
        testID={`${testID}-message`}
      >
        <Icon source="information-outline" size={22} color={theme.colors.onSecondaryContainer} />
        <Text variant="bodyMedium" style={[styles.text, { color: theme.colors.onSecondaryContainer }]}>
          {t('gpsContractDeclinedMessage')}
        </Text>
      </View>
      {errorMessage ? (
        <Text
          variant="bodyMedium"
          accessibilityRole="alert"
          style={[styles.error, { color: theme.colors.error }]}
          testID={`${testID}-switch-error`}
        >
          {errorMessage}
        </Text>
      ) : null}
      {hasActions && (
        <View style={styles.actions}>
          {onContinueHoursOnly && (
            <Button
              mode="contained"
              icon="clock-outline"
              onPress={onContinueHoursOnly}
              loading={busy}
              disabled={busy}
              contentStyle={content}
              testID={`${testID}-hours-only`}
            >
              {t('gpsContractDeclinedHoursOnly')}
            </Button>
          )}
          {onReviewContract && (
            <Button
              mode="outlined"
              icon="file-document-outline"
              onPress={onReviewContract}
              disabled={busy}
              contentStyle={content}
              testID={`${testID}-review-contract`}
            >
              {t('gpsContractDeclinedReview')}
            </Button>
          )}
          {onReturnToChat && (
            <Button mode="text" onPress={onReturnToChat} disabled={busy} contentStyle={content} testID={`${testID}-back-to-chat`}>
              {t('returnToChat')}
            </Button>
          )}
        </View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  root: {
    width: '100%',
    gap: 16,
  },
  message: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 12,
    padding: 16,
    borderRadius: 12,
  },
  text: {
    flex: 1,
    lineHeight: 22,
  },
  actions: {
    gap: 8,
  },
  error: {
    textAlign: 'center',
  },
});
